import qs from 'qs'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useParams } from 'react-router'
import { EMPTY, from } from 'rxjs'
import { catchError, finalize, takeUntil } from 'rxjs/operators'
import { CampaignApi } from 'src/api'
import { axiosHiringApi } from 'src/api/axios'
import { CommunityApi } from 'src/api/community'
import { BreadcrumbHeading, Button, Spinner, useAnalytic } from 'src/components'
import { ETrackingEvent } from 'src/enums'
import { useBehaviorMapper, useDebounce, useDebouncedLoading, useUnsubscribe } from 'src/hooks'
import { IconSuitcase, IconVideoIllustration } from 'src/icons'
import { ICampaignModel, ICommunityModel } from 'src/interfaces'
import { Breadcrumbs } from 'src/layouts/breadcrumbs'
import { ModalAuthentication } from 'src/partials'
import { ModalCampaignDetail } from 'src/partials/modal-campaign-detail'
import { ModalWelcomeToCommunity } from 'src/partials/modal-welcome-to-community'
import { browserHistory, ERoutes, generate } from 'src/router'
import { DialogService, LoadMoreService } from 'src/services'
import { AuthModule } from 'src/store'
import { CommunityUtils } from 'src/utils/community.utils'
import { JobCard } from '../components/job-card'
import Style from './style.module.scss'

const AutoJoinAnchor: FC = () => {
  const unsubscribe$ = useUnsubscribe()
  const { state } = useParams<{ state: string }>()
  const isAuthenticated = useBehaviorMapper(AuthModule.isAuthenticated$)
  const communities = useBehaviorMapper(AuthModule.communities$)
  const [community, setCommunity] = useState<ICommunityModel>()

  useEffect(() => {
    if (state) {
      from(CommunityApi.communities({ code: state }))
        .pipe(
          takeUntil(unsubscribe$),
          catchError(() => EMPTY)
        )
        .subscribe(({ data }) => {
          const community = data?.rows?.[0]
          if (community) {
            setCommunity(community)
          }
        })
    }
  }, [state, unsubscribe$])

  const userCommunity = useMemo(() => {
    if (!community) {
      return
    }
    return communities.find((c) => c.code === community.code)?.userCommunity
  }, [community, communities])

  const isFirstTimeViewCommunity = useMemo(() => {
    return userCommunity && userCommunity.createdAt === userCommunity.updatedAt
  }, [userCommunity])

  useEffect(() => {
    if (community?.code && isFirstTimeViewCommunity) {
      DialogService.open(ModalWelcomeToCommunity, { community })
      from(CommunityApi.updateJoinedCommunity(community.code))
        .pipe(
          takeUntil(unsubscribe$),
          catchError(() => EMPTY),
          finalize(() => {
            userCommunity && (userCommunity.updatedAt = new Date().toISOString())
          })
        )
        .subscribe()
    }
  }, [community, isFirstTimeViewCommunity, unsubscribe$, userCommunity])

  useDebounce(() => {
    const communityCode = community?.code
    if (!communityCode) {
      return
    }
    if (isAuthenticated && !userCommunity) {
      from(AuthModule.joinCommunity({ communityCode, joinSource: 'view' }))
        .pipe(catchError(() => EMPTY))
        .subscribe()
    } else if (!isAuthenticated) {
      DialogService.open(ModalWelcomeToCommunity, { community })
    }
  }, 1000, [isAuthenticated, community?.code, userCommunity, community])

  return null
}

const EmptyState: FC = () => {
  return (
    <div className="fx-1 fx-column fx-center gap-10">
      <div className="fx-column fx-ai-center gap-5">
        <IconVideoIllustration size={120}/>
        <div className="bold-24 fw-700 txt-center txt-black-01">
          Nothing Here Yet
        </div>
        <div className="body-14 txt-black-02 txt-center" style={{ maxWidth: 390 }}>
          Check back later or be the first to add a job and get the vibes rolling!
        </div>
      </div>
      <Button
        className="px-6 py-3 gap-2"
        onClick={() => browserHistory.push(generate(ERoutes.CLAIM_JOBS))}
      >
        <IconSuitcase/>
        Add Your Job
      </Button>
    </div>
  )
}

export const CommunityState: FC = () => {
  const { state } = useParams<{ state: string }>()
  const [keyword] = useState<string>()
  const { eventHandler } = useAnalytic('community')
  const isAuthenticated = useBehaviorMapper(AuthModule.isAuthenticated$)
  const communities = useBehaviorMapper(AuthModule.communities$)

  const _loadMoreService = useMemo(() => new LoadMoreService<ICampaignModel>({
    axiosInstance: axiosHiringApi,
    endpoint: CampaignApi._myCommunity + '?' + qs.stringify({
      keyword,
      communityCode: state
    })
  }), [keyword, state])

  const { ref: refLoadMore, inView } = useInView({ threshold: 0 })
  const loading = useBehaviorMapper(_loadMoreService.loading$)
  const items = useBehaviorMapper(_loadMoreService.items$)

  useEffect(() => {
    if (inView && !loading && _loadMoreService.hasMore) {
      _loadMoreService.loadMore()
    }
  }, [_loadMoreService, inView, loading])

  useEffect(() => {
    _loadMoreService.loadMore()
  }, [_loadMoreService])

  const debounceLoading = useDebouncedLoading(!items.length && loading)
  const communityName = useMemo(
    () => communities.find((c) => c.code === state)?.name || CommunityUtils.getNameByCode(state),
    [communities, state]
  )

  const onAddYourJob = useCallback(() => {
    if (isAuthenticated) {
      browserHistory.push(generate(ERoutes.CLAIM_JOBS))
    } else {
      DialogService.open(ModalAuthentication, { stage: 'sign-in' })
    }
  }, [isAuthenticated])

  return (
    <>
      <AutoJoinAnchor/>

      <Breadcrumbs>
        <div className="fx fx-ai-center gap-6">
          <BreadcrumbHeading active>{communityName} Job Community</BreadcrumbHeading>
        </div>
      </Breadcrumbs>

      <div className="fx-1 fx-column gap-5 p-8 bg-neutral-20">
        <div className="fx-1 fx-column gap-4 overflow-auto relative">
          {!!items.length && (
            <div className={Style.jobsGrid}>
              <div className={Style.addJobBox}>
                <Button
                  className="px-6 py-3 gap-2"
                  onClick={eventHandler(ETrackingEvent.BTN_ADD_YOUR_JOB, onAddYourJob)}
                >
                  <IconSuitcase/>
                  Add Your Job
                </Button>
              </div>
              {items.map((campaign) => (
                <JobCard
                  key={campaign.id}
                  analyticScreen="community"
                  campaign={campaign}
                  onDetail={() => DialogService.open(ModalCampaignDetail, { campaign })}
                />
              ))}
            </div>
          )}
          {!items.length && !debounceLoading && (
            <EmptyState/>
          )}
          {debounceLoading && (
            <div className="fx-1 fx fx-center">
              <Spinner/>
            </div>
          )}
          <div ref={refLoadMore}/>
        </div>
      </div>
    </>
  )
}
